import { AISummary } from "@/components/ai/AISummary.tsx";
import { FormAggregatedResultElement } from "@/components/form-result/aggregated-result/components/FormAggregatedResultElement.tsx";
import { FormAggregatedResultFilterOptions } from "@/components/form-result/aggregated-result/components/types.ts";
import { ApiAIProcessingStatus } from "@/types/ai/ai.types.ts";
import { ApiFormAggregationResult } from "@/types/forms/form-result.types.ts";
import { ApiForm, ApiFormViewElement } from "@/types/forms/form-view.types.ts";
import { Avatar, Box, EmptyStateBase, Stack, Text, Title } from "@skailer-com/uikit";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface FormAggregatedResultProps {
  form: ApiForm;
  results: ApiFormAggregationResult;
  showDetailModalCallback?: (
    element: ApiFormViewElement,
    filterOptions?: FormAggregatedResultFilterOptions
  ) => void;
  show_more?: boolean;
  disableMaxWidth?: boolean;
  hideAISummary?: boolean;
}

export const FormAggregatedResult = ({
  form,
  results,
  showDetailModalCallback,
  show_more = false,
  disableMaxWidth = false,
  hideAISummary = false,
}: FormAggregatedResultProps) => {
  const { t } = useTranslation();
  if (results.is_below_anonymity_threshold) {
    return (
      <EmptyStateBase
        emoji={":lock:"}
        title={t("surveys:result_not_available_yet_title")}
        description={t("surveys:result_not_available_yet_description")}
      />
    );
  }

  return (
    <>
      {!hideAISummary &&
        results.ai_summary &&
        results.ai_processing_status == ApiAIProcessingStatus.completed && (
          <Box className={"ml-34 py-4"}>
            <AISummary ai_summary={results.ai_summary} showMore={show_more} />
          </Box>
        )}
      {!hideAISummary &&
        [ApiAIProcessingStatus.waiting, ApiAIProcessingStatus.evaluating].includes(
          results.ai_processing_status
        ) &&
        (() => {
          const isWaiting = results.ai_processing_status === ApiAIProcessingStatus.waiting;

          return (
            <Box className={"ml-34 py-4"}>
              <Stack
                gap={"sm"}
                className={twMerge(
                  "border rounded-xs p-4",
                  isWaiting ? "border-dark/40 bg-dark/10" : "border-violet/40 bg-violet/10"
                )}
                style={{
                  boxShadow: isWaiting
                    ? `0px 16px 96px 0px color-mix(in srgb, var(--color-dark) 24%, transparent), 0px 4px 8px 0px hsla(0, 0%, 0%, 0.08)`
                    : `0px 16px 96px 0px color-mix(in srgb, var(--color-violet) 24%, transparent), 0px 4px 8px 0px hsla(0, 0%, 0%, 0.08)`,
                }}
              >
                <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
                  <Avatar
                    emoji={isWaiting ? ":sleeping:" : ":brain:"}
                    className={twMerge(isWaiting ? "bg-dark/60" : "bg-violet/60")}
                  />
                  <Text
                    className={twMerge("font-semibold", isWaiting ? "text-dark" : "text-violet")}
                  >
                    {isWaiting ? t("ai:ai_waiting_title") : t("ai:ai_evaluating_title")}
                  </Text>
                </Stack>
                <Text className={twMerge(isWaiting ? "text-primary" : "text-violet")}>
                  {isWaiting ? t("ai:ai_waiting_description") : t("ai:ai_evaluating_description")}
                </Text>
              </Stack>
            </Box>
          );
        })()}

      {form.pages.map((page, pageIndex) => {
        const onePage = form.pages.length === 1;
        const oneSection = page.sections.length === 1;

        return (
          <Fragment key={`${pageIndex}`}>
            {!onePage && (
              <Stack direction={"horizontal"} gap={"lg"}>
                <Stack items={"end"} className={"w-28 min-h-7 pb-0.5"} justify={"end"}>
                  <Title size={"xs"} header={`${pageIndex + 1}`} />
                </Stack>
                <Stack className={"flex-1"} justify={"end"}>
                  <Title
                    header={page.title || t("common:page")}
                    size={"lg"}
                    paddingTop={true}
                    className={page.title ? "" : "text-secondary"}
                  />
                </Stack>
              </Stack>
            )}

            {page.sections.map((section, sectionIndex) => {
              return (
                <Fragment key={`${sectionIndex}`}>
                  {!oneSection && (
                    <Stack direction={"horizontal"} gap={"lg"}>
                      <Stack items={"end"} className={"w-28 min-h-7 pb-0.5"} justify={"end"}>
                        <Title
                          size={"xs"}
                          header={`${onePage ? "" : pageIndex + 1 + "."}${sectionIndex + 1}`}
                        />
                      </Stack>
                      <Stack className={"flex-1"} justify={"end"}>
                        <Title
                          header={section.title || t("common:section")}
                          size={"md"}
                          paddingTop={true}
                          className={section.title ? "" : "text-secondary"}
                        />
                      </Stack>
                    </Stack>
                  )}
                  {section.elements.map((element, elementIndex) => {
                    return (
                      <Fragment key={`${elementIndex}`}>
                        <Stack direction={"horizontal"} gap={"lg"}>
                          <Stack
                            items={"end"}
                            className={"w-28 min-h-7 pt-6 shrink-0"}
                            justify={"start"}
                          >
                            <Title
                              size={"xs"}
                              header={`${onePage ? "" : pageIndex + 1 + "."}${oneSection ? "" : sectionIndex + 1 + "."}${elementIndex + 1}`}
                            />
                          </Stack>
                          <div className="print:break-inside-avoid flex-1">
                            <Stack
                              className={twMerge(
                                "flex-1 w-full pt-6",
                                disableMaxWidth ? "" : "max-w-[640px]"
                              )}
                              justify={"end"}
                            >
                              <FormAggregatedResultElement
                                element={element}
                                results={results}
                                showDetailModalCallback={showDetailModalCallback}
                                hideAISummary={hideAISummary}
                                showMore={show_more}
                              />
                            </Stack>
                          </div>
                        </Stack>
                      </Fragment>
                    );
                  })}
                </Fragment>
              );
            })}
          </Fragment>
        );
      })}
    </>
  );
};
